import React, { useEffect, useState } from 'react';

import classNames from 'classnames';

import {
	PageModule,
	Survey,
	Session,
	SurveyType
} from "../../../../../types/working-model";
import { getStylingOverrides } from "../../../utils";
import { TypographyItem } from "../../../../general-ui/typography-item/typography-item";
import EngageCard from '../../engage-section/engage-card';
import QuestionsCard from '../questions/questions-card';
import Question from './question';
import { getTextValue } from "../../../../../utils/utils";
import useTranslationRoute from '../../../hooks/use-translation-route';
import { useTranslate } from '../../../../../i18n/useTranslationModules';
import { useTypedSelector, useAppDispatch } from "../../../../../store/reducers/use-typed-selector";
import { checkQuizzesForCompletion } from '../../../../../store/actions/event/event-actions';
import { OptionalComponent } from '../../../../../utils/optional-component';
import { useIsNewModuleGrouping } from '../../../../../hooks/session.hooks';
import WaitingIndicator from '../../../../general-ui/waiting-indicator/waiting-indicator';

import '../../../../../scss/live-event/base/session/survey.scss';
import { useInView } from 'react-intersection-observer';
import { broadcastSignal } from 'utils/event-emitter';
import { useLanguageParam } from '@/components/live-event/utils';

interface SurveyProps {
	module?: PageModule;
	template: string;
	sessionUuid?: string;
	preview?: boolean;
	surveySubmoduleQuestions?: PageModule;
	session?: Session;
}

export {
	getPollAnswerLocalStorageKey,
	getPollAnswerLocalStorageKeyBackwardsCompatible,
	calculateFinishedSurvey,
} from './survey.ts';
export type { CompletedQuizQuestion } from './survey.ts';

export default function SessionSurvey({
	module,
	template,
	sessionUuid,
	preview,
	surveySubmoduleQuestions,
	session,
}: SurveyProps): JSX.Element | null {
	const dispatch = useAppDispatch();
	const [activeEngageCard, setActiveEngageCard] = useState<number>();
	const language = useLanguageParam();
	const { content, styling_overrides } = module || {};
	const stylingOverrides = getStylingOverrides(styling_overrides);
	const namespace = useTranslationRoute();
	const { t } = useTranslate(namespace);
	const isModuleGroupingV2 = useIsNewModuleGrouping() && !!session;
	const { ref, inView } = useInView();

	const sessions = useTypedSelector((state) => state.LiveEventReducer.eventBundle?.sessions);
	const event = useTypedSelector((state) => state.LiveEventReducer.eventBundle?.event);
	const blProfileUserToken = useTypedSelector((state) => state.LiveEventReducer.blProfileUserToken);
	const quizProgressSettled = useTypedSelector((state) => !!session && state.SurveysReducer.quizProgressSettledSessions.includes(session.session));
	const awaitingQuizProgress = isModuleGroupingV2 && !preview && !!blProfileUserToken && !quizProgressSettled;

	const {
		backgroundColor,
		backgroundImg,
		customHeadingOverrides,
		moduleTitleOverrides,
		titleOverrides,
	} = stylingOverrides;

	const { custom_heading, title } = getTextValue(content, language);

	// This checks quizzes for completion; does not set completed answer values
	useEffect(() => {
		if (!event || !sessions || !module) return;
		dispatch(checkQuizzesForCompletion(module, event, sessions));
	}, [dispatch, event, module, sessions, quizProgressSettled]);

	useEffect(() => {
		broadcastSignal('engage-in-view', inView);
	}, [inView]);

	const modules = module?.modules;
	const surveysCustomHeaderTitle = t(`${namespace}:survey.${module?.id}.title`, t(`${namespace}:survey.title`, title));

	if (!module?.is_on || (!modules?.length && !isModuleGroupingV2)) return null;
	return (
		<section
			className={classNames(
				'surveys session-module surveys-content-home',
				template,
				backgroundColor
			)}
			style={backgroundImg}
			ref={ref}
		>
			<div className={classNames({ 'module-container': !sessionUuid, 'engage-grid': isModuleGroupingV2 })}>
				<OptionalComponent display={!isModuleGroupingV2}>
					<>
						<TypographyItem
							className={classNames(
								'surveys-custom-heading',
								customHeadingOverrides,
								{ 'surveys-custom-heading-event': !sessionUuid })}
							tagName="p"
						>
							{t(`${namespace}:survey.${module?.id}.heading`, t(`${namespace}:survey.heading`, custom_heading))}
						</TypographyItem>

						<OptionalComponent display={!!surveysCustomHeaderTitle}>
							<TypographyItem className={classNames('evt-heading-2 surveys-custom-header-title', titleOverrides)} tagName="h2">
								{surveysCustomHeaderTitle}
							</TypographyItem>
						</OptionalComponent>
					</>
				</OptionalComponent>

				{module && !!modules?.length && modules.map((survey: Survey, index) => {
					if (isModuleGroupingV2) {
						if (awaitingQuizProgress && survey.survey_type === SurveyType.quiz) {
							return (
								<div key={survey.survey} className="engage-card engage-card-loading" aria-busy="true">
									<WaitingIndicator />
								</div>
							);
						}

						return (
							<EngageCard
								key={survey.survey}
								engageCardId={survey.survey}
								sessionUuid={sessionUuid}
								survey={survey}
								module={module}
								template={template}
								preview={preview}
								isEditor={preview}
								activeEngageCard={activeEngageCard}
								order={index + 2}
								setActiveEngageCard={setActiveEngageCard}
							/>
						);
					}

					const surveyTitle = t(`survey.${survey.survey}.name`, survey?.name?.[language] as string ?? survey?.name?.base);

					return (
						<div key={`survey-container-${survey.survey}`} className={classNames('survey-container')}>
							<TypographyItem
								className={classNames('survey-title evt-heading-4', template, moduleTitleOverrides)}
								tagName="p"
							>
								{surveyTitle}
							</TypographyItem>

							<Question
								sessionUuid={sessionUuid}
								survey={survey}
								module={module}
								template={template}
							/>
						</div>
					);
				})}
			</div>
		</section>
	);
}
